package com.schoolconnect.app.feature.itusers

import com.schoolconnect.app.datasource.network.response.ITUsersAnalyticsResponse
import com.schoolconnect.app.feature.common.ServerPagedListUiState
import com.schoolconnect.app.feature.common.ServerPagingState
import com.schoolconnect.app.feature.common.compareBySmartText

data class ITUsersFilterFormValues(
    val searchQuery: String = "",
    val role: String = "",
    val status: String = "",
    val createdFrom: String = "",
    val createdTo: String = ""
)

enum class ITUserRoleUi {
    SUPER_ADMIN,
    IT_ADMIN,
    IT_SUPPORT
}

enum class ITUserStatusUi {
    ACTIVE,
    INACTIVE
}

enum class ITUsersSortField(val apiField: String) {
    USER("name"),
    MOBILE("mobile"),
    EMAIL("email"),
    ROLE("role_id"),
    CREATED_ON("created_at"),
    STATUS("is_active")
}

data class ITUserRowUi(
    val id: String,
    val userId: String = "",
    val assignmentId: String = "",
    val name: String,
    val mobile: String,
    val email: String,
    val role: ITUserRoleUi?,
    val status: ITUserStatusUi,
    val createdRank: Int = 0,
    val createdOn: String = "",
    val updatedOn: String = ""
)

data class ITUserFormValues(
    val name: String = "",
    val mobile: String = "",
    val email: String = "",
    val role: String = "",
    val status: String = "Active"
)

data class ITUserFormErrors(
    val name: String = "",
    val mobile: String = "",
    val email: String = "",
    val role: String = "",
    val status: String = ""
)

data class ITUsersUiState(
    override val paging: ServerPagingState = ServerPagingState(),
    val isLoading: Boolean = false,
    val isAnalyticsLoading: Boolean = false,
    val errorMessage: String = "",
    val infoMessage: String = "",
    val isAddUserModalOpen: Boolean = false,
    val isAddUserSaving: Boolean = false,
    val isEditUserSaving: Boolean = false,
    val editSaveCompletedNonce: Int = 0,
    val searchQuery: String = "",
    val roleFilter: String = "",
    val statusFilter: String = "",
    val createdFrom: String = "",
    val createdTo: String = "",
    val sortField: ITUsersSortField = ITUsersSortField.USER,
    val sortAscending: Boolean = true,
    val users: List<ITUserRowUi> = emptyList(),
    val analytics: ITUsersAnalyticsResponse? = null
) : ServerPagedListUiState

sealed interface ITUsersUiEvent {
    data object BackClicked : ITUsersUiEvent
    data object RefreshRequested : ITUsersUiEvent
    data object AddUserClicked : ITUsersUiEvent
    data object AddUserModalClosed : ITUsersUiEvent
    data class SearchChanged(val query: String) : ITUsersUiEvent
    data class SortChanged(val field: ITUsersSortField) : ITUsersUiEvent
    data class PageChanged(val pageIndex: Int) : ITUsersUiEvent
    data class PageSizeChanged(val pageSize: Int) : ITUsersUiEvent
    data class FiltersApplied(val values: ITUsersFilterFormValues) : ITUsersUiEvent
    data object FiltersReset : ITUsersUiEvent
    data class ToggleStatus(val userId: String) : ITUsersUiEvent
    data class DeleteUser(val userId: String) : ITUsersUiEvent
    data class SaveUser(val userId: String?, val values: ITUserFormValues) : ITUsersUiEvent
    data object ClearMessages : ITUsersUiEvent
}

internal const val IT_ROLE_ID_SUPER_ADMIN = 1
internal const val IT_ROLE_ID_IT_ADMIN = 2
internal const val IT_ROLE_ID_IT_SUPPORT = 3

internal val itUserRoleOptions = listOf(
    "IT Super Admin",
    "IT Admin",
    "IT Support"
)

internal val itUserStatusOptions = listOf(
    "Active",
    "Inactive"
)

internal fun itUsersFilterLabels(state: ITUsersUiState): List<String> = buildList {
    // Role/status are on the toolbar dropdowns — only chip search + more-filters dates.
    if (state.searchQuery.isNotBlank()) add("Search: ${state.searchQuery.trim()}")
    if (state.createdFrom.isNotBlank() || state.createdTo.isNotBlank()) {
        val from = state.createdFrom.ifBlank { "Any" }
        val to = state.createdTo.ifBlank { "Any" }
        add("Created: $from to $to")
    }
}

internal fun roleLabel(role: ITUserRoleUi?): String = when (role) {
    ITUserRoleUi.SUPER_ADMIN -> "IT Super Admin"
    ITUserRoleUi.IT_ADMIN -> "IT Admin"
    ITUserRoleUi.IT_SUPPORT -> "IT Support"
    null -> "—"
}

internal fun roleFromLabel(label: String): ITUserRoleUi? = when (
    label.trim().uppercase()
        .replace('_', ' ')
        .replace('-', ' ')
        .replace(Regex("\\s+"), " ")
) {
    "SUPER ADMIN", "SUPERADMIN", "IT SUPER ADMIN", "IT SUPERADMIN" -> ITUserRoleUi.SUPER_ADMIN
    "IT ADMIN", "ITADMIN" -> ITUserRoleUi.IT_ADMIN
    "IT SUPPORT", "ITSUPPORT" -> ITUserRoleUi.IT_SUPPORT
    "ADMIN" -> ITUserRoleUi.IT_ADMIN
    "SUPPORT" -> ITUserRoleUi.IT_SUPPORT
    else -> null
}

internal fun statusLabel(status: ITUserStatusUi): String = when (status) {
    ITUserStatusUi.ACTIVE -> "Active"
    ITUserStatusUi.INACTIVE -> "Inactive"
}

internal fun statusFromLabel(label: String): ITUserStatusUi? = when (label.trim().lowercase()) {
    "active" -> ITUserStatusUi.ACTIVE
    "inactive" -> ITUserStatusUi.INACTIVE
    "disabled" -> ITUserStatusUi.INACTIVE
    else -> null
}

internal fun itUserRoleId(role: ITUserRoleUi): Int = when (role) {
    ITUserRoleUi.SUPER_ADMIN -> IT_ROLE_ID_SUPER_ADMIN
    ITUserRoleUi.IT_ADMIN -> IT_ROLE_ID_IT_ADMIN
    ITUserRoleUi.IT_SUPPORT -> IT_ROLE_ID_IT_SUPPORT
}

internal fun itUserRoleFromId(roleId: Int?): ITUserRoleUi? = when (roleId) {
    IT_ROLE_ID_SUPER_ADMIN -> ITUserRoleUi.SUPER_ADMIN
    IT_ROLE_ID_IT_ADMIN -> ITUserRoleUi.IT_ADMIN
    IT_ROLE_ID_IT_SUPPORT -> ITUserRoleUi.IT_SUPPORT
    else -> null
}

internal fun itUsersComparator(
    field: ITUsersSortField,
    ascending: Boolean
): Comparator<ITUserRowUi> {
    val comparator = when (field) {
        ITUsersSortField.USER -> compareBySmartText<ITUserRowUi> { it.name }
        ITUsersSortField.MOBILE -> compareBySmartText<ITUserRowUi> { it.mobile }
        ITUsersSortField.EMAIL -> compareBySmartText<ITUserRowUi> { it.email }
        ITUsersSortField.ROLE -> compareBy<ITUserRowUi> {
            when (it.role) {
                ITUserRoleUi.SUPER_ADMIN -> 0
                ITUserRoleUi.IT_ADMIN -> 1
                ITUserRoleUi.IT_SUPPORT -> 2
                null -> 3
            }
        }
        ITUsersSortField.CREATED_ON -> compareBy<ITUserRowUi> { it.createdRank }
        ITUsersSortField.STATUS -> compareBy<ITUserRowUi> {
            when (it.status) {
                ITUserStatusUi.ACTIVE -> 0
                ITUserStatusUi.INACTIVE -> 1
            }
        }
    }
    return if (ascending) comparator else comparator.reversed()
}

internal fun validateITUser(values: ITUserFormValues): ITUserFormErrors {
    val normalizedMobile = sanitizeITUserMobile(values.mobile)
    return ITUserFormErrors(
        name = if (values.name.trim().isBlank()) "Enter user name" else "",
        mobile = if (isValidITUserMobile(normalizedMobile)) "" else "Enter a valid Indian mobile number",
        email = if (isValidITUserEmail(values.email)) "" else "Enter a valid email",
        role = if (roleFromLabel(values.role) == null) "Select a role" else "",
        status = if (statusFromLabel(values.status) == null) "Select a status" else ""
    )
}

internal fun isValidITUserEmail(raw: String): Boolean = IT_USER_EMAIL_REGEX.matches(raw.trim())

internal fun sanitizeITUserMobile(raw: String): String {
    val trimmed = raw.trim()
    if (trimmed.isBlank()) return ""

    val normalized = buildString(trimmed.length) {
        trimmed.forEachIndexed { index, char ->
            when {
                char.isDigit() -> append(char)
                char == '+' && index == 0 -> append(char)
            }
        }
    }

    return normalized
}

internal fun isValidITUserMobile(raw: String): Boolean {
    val normalized = sanitizeITUserMobile(raw)
    val digitsOnly = normalized.filter { it.isDigit() }
    if (digitsOnly.length == 10) {
        return digitsOnly.firstOrNull()?.let { it in '6'..'9' } == true
    }

    if (normalized.startsWith("+91")) {
        val localDigits = normalized.removePrefix("+91")
        return localDigits.length == 10 && localDigits.firstOrNull()?.let { it in '6'..'9' } == true
    }

    if (normalized.startsWith("91") && normalized.length == 12) {
        val localDigits = normalized.removePrefix("91")
        return localDigits.length == 10 && localDigits.firstOrNull()?.let { it in '6'..'9' } == true
    }

    return false
}

private val IT_USER_EMAIL_REGEX = Regex("^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,}$")
